/**
 * ConfirmationDialog Component
 *
 * A dialog component that displays a confirmation message with confirm/cancel actions.
 * Built on top of Material-UI's Popper component for flexible positioning.
 *
 * @example
 * Basic usage:
 * ```tsx
 * <ConfirmationDialog
 *   anchorRef={buttonRef}
 *   showDialog={isOpen}
 *   dialogContent="Are you sure you want to delete this item?"
 *   confirmText="Yes, delete"
 *   cancelText="Cancel"
 *   onConfirm={handleDelete}
 *   onCancel={handleCancel}
 * />
 * ```
 *
 * @example
 * With custom positioning:
 * ```tsx
 * <ConfirmationDialog
 *   anchorRef={buttonRef}
 *   showDialog={isOpen}
 *   dialogContent="Are you sure?"
 *   placement="top"  // Override default 'bottom-end' placement
 *   onConfirm={handleConfirm}
 * />
 * ```
 *
 * @example
 * With custom modifiers (advanced):
 * ```tsx
 * <ConfirmationDialog
 *   anchorRef={buttonRef}
 *   showDialog={isOpen}
 *   dialogContent="Confirm action?"
 *   modifiers={{
 *     flip: { enabled: false },  // Disable auto-flipping
 *     offset: { offset: '0,10' }  // Add 10px vertical offset
 *   }}
 *   onConfirm={handleConfirm}
 * />
 * ```
 *
 * Positioning:
 * - Default placement: 'bottom-end' (bottom-right of anchor element)
 * - Default modifiers: Includes flip behavior and viewport overflow prevention
 * - Flip behavior: Tries bottom-end → top-end → bottom → top
 * - Viewport padding: 8px from edges
 *
 * All Material-UI PopperProps can be passed to customize positioning, transitions, etc.
 * The component omits 'open', 'anchorEl', and 'children' props as they are managed internally.
 */

import type { Theme, PopperProps } from '@material-ui/core';
import {
    ClickAwayListener,
    Popper,
    Paper,
    IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import { Block } from '@klp/design/Block';
import React from 'react';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import zIndex from '@material-ui/core/styles/zIndex';
import { ActionButton } from '@klp/design/Action';

type ConfirmationDialogProps = {
    anchorRef: React.RefObject<HTMLElement>;
    showDialog: boolean;
    confirmText?: string;
    cancelText?: string;
    dialogContent: string;
    isLoading?: boolean;
    onConfirm?: () => void;
    onCancel?: () => void;
};

type ConfirmationDialogWithPopperProps = ConfirmationDialogProps &
    Omit<PopperProps, 'open' | 'anchorEl' | 'children'>;

const styles = (theme: Theme) => ({
    popper: {
        zIndex: zIndex.modal + 1,
        maxWidth: '350px',
    },
    paper: {
        padding: theme.spacing(2),
        textAlign: 'center',
    },
    contentBlock: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    buttonBlock: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& button': {
            marginLeft: theme.spacing(2),
            marginRight: theme.spacing(2),
        },
    },
});

export const ConfirmationDialog: React.FC<
    ConfirmationDialogWithPopperProps
> = ({
    anchorRef,
    showDialog,
    confirmText,
    cancelText,
    dialogContent,
    isLoading = false,
    onConfirm,
    onCancel,
    placement = 'bottom-end',
    modifiers = {
        flip: {
            enabled: true,
            behavior: ['bottom-end', 'top-end', 'bottom', 'top'],
        },
        preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
            padding: 8,
        },
    },
    ...restProps
}) => {
    const classes = useKlpStyles({}, styles);

    const handleCompleteActivity = async () => {
        if (onConfirm) {
            await onConfirm();
        }
    };

    const handleAbortActivity = async () => {
        if (onCancel) {
            await onCancel();
        }
    };

    if (!showDialog) return null;

    return (
        <ClickAwayListener onClickAway={handleAbortActivity}>
            <Popper
                open={showDialog}
                anchorEl={anchorRef.current}
                placement={placement}
                transition={true}
                className={classes.popper}
                modifiers={modifiers}
                {...restProps}
            >
                <Paper className={classes.paper}>
                    <Block className={classes.contentBlock}>
                        {dialogContent}
                    </Block>
                    <Block className={classes.buttonBlock}>
                        {cancelText && (
                            <ActionButton
                                onClick={handleAbortActivity}
                                actionType="cancel"
                            >
                                {cancelText}
                            </ActionButton>
                        )}
                        {!cancelText && (
                            <IconButton
                                onClick={handleAbortActivity}
                                color="secondary"
                            >
                                <CloseIcon />
                            </IconButton>
                        )}

                        {confirmText && (
                            <ActionButton
                                onClick={handleCompleteActivity}
                                color="primary"
                                actionType="forward"
                                isLoading={isLoading}
                            >
                                {confirmText && confirmText}
                            </ActionButton>
                        )}

                        {!cancelText && (
                            <IconButton
                                onClick={handleCompleteActivity}
                                color="primary"
                            >
                                <CheckIcon />
                            </IconButton>
                        )}
                    </Block>
                </Paper>
            </Popper>
        </ClickAwayListener>
    );
};
